import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Switch } from "../components/ui/switch";
import { Portfolio, Property, User, FinancialAccount } from "../../global-types";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Users } from "../components/Users";
import { Properties } from "../components/Properties";
import { Portfolios } from "../components/Portfolios";
import { FinancialAccounts } from "../components/FinancialAccounts";
import { useAccountDetails } from "@/hooks/queryHooks";

const AccountDetails: React.FC = () => {
  const location = useLocation();
  const accountId = location.state?.accountId;

  const [activeTab, setActiveTab] = useState<"portfolio" | "property" | "user" | "financial">(
    "portfolio",
  );
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [financialAccounts, setFinancialAccounts] = useState<FinancialAccount[]>([]);
  const [showAllColumns, setShowAllColumns] = useState(false)

  const { query, invalidateCb } = useAccountDetails(accountId);
  const { data, isPending, error } = query;

  const handlePortfolioCreated = () => {
    invalidateCb();
  };

  const handlePortfolioUpdated = () => {
    invalidateCb();
  };

  useEffect(() => {
    if (data) {
      console.log("AccountDetails data received:", data);
      setUsers(data.users);
      setProperties(data.properties);
      setPortfolios(data.portfolios);
      setFinancialAccounts(data.financial_accounts || []);
    }
  }, [data]);

  if (isPending) {
    return <div className="p-4">Loading account details...</div>;
  }

  if (error) {
    return (
      <div className="p-4 text-red-600">
        Error loading account details: {error.message}
      </div>
    );
  }

  return (
    <div className="py-4 px-[5%]">
      <Tabs
        defaultValue="property"
        className="w-full relative content-center"
        value={activeTab}
        onValueChange={(value) =>
          setActiveTab(value as "portfolio" | "property" | "user" | "financial")
        }
      >
        <TabsList className="grid grid-cols-4 w-1/3 min-w-65 mx-auto">
          <TabsTrigger value="portfolio">Portfolios</TabsTrigger>
          <TabsTrigger value="property">Properties</TabsTrigger>
          <TabsTrigger value="user">Users</TabsTrigger>
          <TabsTrigger value="financial">Financial Accounts</TabsTrigger>
        </TabsList>

        {activeTab === "property" && properties.length > 0 &&
          (
            <div className="right-0 absolute">
              <div className="flex items-center gap-2">
                <Switch checked={showAllColumns} onCheckedChange={setShowAllColumns} />
                <span>Show Details</span>
              </div>
            </div>
          )}

        <Users users={users} accountId={accountId} />
        <Properties portfolios={portfolios} properties={properties} showDetailedView={showAllColumns} />
        <Portfolios
          accountId={accountId}
          portfolios={portfolios}
          onPortfolioCreated={handlePortfolioCreated}
          onPortfolioUpdated={handlePortfolioUpdated}
        />
        <FinancialAccounts financialAccounts={financialAccounts} accountId={accountId} />
      </Tabs>
    </div>
  );
};

export default AccountDetails;
